import type { SpecClosure } from '@realplc/types';

export interface DecisionCounts {
  /** Slots the engineer decided: answered a card, or stated the value outright. */
  confirmed: number;
  /** Slots a written-down engineering default closed instead of asking. */
  defaulted: number;
  /** Slots nobody decided — the model chose, or the question stayed open. */
  assumed: number;
}

/**
 * Who decided what, from the turn's own accounting.
 *
 * Every decision a requirement needed is a slot, so these three are a partition
 * of one set and cannot contradict each other — which two numbers counted from
 * two different texts always could. Read in two places, defined here once: the
 * line above a generated program, and the spec panel in the workspace. Those
 * two disagreeing about the same project would be worse than neither existing.
 *
 * A recorded default for a slot that is still open is not a default taken — the
 * question was asked anyway — so only closed slots count as one. A slot nobody
 * answered counts with the guesses: the engineer did not decide it either way,
 * and the program was written regardless.
 *
 * Returns undefined when the requirement needed no decisions at all, which is
 * not the same as having no closure to read.
 */
export function decisionCountsFromClosure(closure: SpecClosure | undefined): DecisionCounts | undefined {
  if (!closure) return undefined;

  const closed = new Set(closure.closedSlots);
  const defaults = new Set(closure.defaultsTaken);
  const defaulted = [...closed].filter(key => defaults.has(key)).length;
  const confirmed = closed.size - defaulted;
  const assumed = new Set(closure.requiredSlots.filter(key => !closed.has(key))).size;

  if (confirmed === 0 && assumed === 0 && defaulted === 0) return undefined;
  return { confirmed, defaulted, assumed };
}
